<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue';
import i18n from '@/localization/i18n';

import BusinessRequestProgressBar from "./BusinessRequestProgressBar.vue";
import { storeToRefs } from 'pinia';
import { useBusinessRequestFormStore } from '@/pages/index/stores/business-request-form-store';
import { getDescriptionFromOpenAI } from '@/services/modules/BusinessRequestService';
import { logNewEvent } from '@/services/modules/firebase';

const businessRequestFormStore = useBusinessRequestFormStore()
const { descriptionFormData,titleFormData,addressFormData,urgencyFormData,lastTitleUsed } = storeToRefs(businessRequestFormStore)

const t = i18n.global.t

const isLoading = ref(false)
const hasFocused = ref(false)

onMounted(async () => {
    //call the open ai description generator only when we don't have a description yet or the title has changed since last generation
    const currentTitle = titleFormData.value.title
    const shouldGenerate =
        (!descriptionFormData.value.description ||
         currentTitle !== lastTitleUsed.value) &&
        !hasFocused.value

    if (!shouldGenerate) {
        if (descriptionFormData.value.description) {
            hasFocused.value = true
        }
        return
    }
    
    try {
        isLoading.value = true
        const description = await descriptionHelper()
        if (description) {
            descriptionFormData.value.description = description
            lastTitleUsed.value = currentTitle   //store the title used for last generation
        }
    } catch (error) {
       // Handle error if needed
    } finally {
        isLoading.value = false
    }

    // if(descriptionFormData.value.description.length == 0){
    //     try {
    //         isLoading.value = true
    //         descriptionHelper().then((description)=>{
    //             if(description) descriptionFormData.value.description = description
    //             isLoading.value = false
    //         })
    //     } catch (error) {
    //         isLoading.value = false
    //     }
    // }else {
    //     hasFocused.value = true
    // }

})

const formErrors = reactive({
   description: '',
})

const validateForm = () =>{
    formErrors.description = !descriptionFormData.value.description ? t("w_msg_frmVal_required") : ""
    if(descriptionFormData.value.description && descriptionFormData.value.description.length < 30){
        formErrors.description = t("w_txt_business_request_description_more_details")
    }

    if(formErrors.description === ''){
        logNewEvent("presta_request_submit_desc")
        businessRequestFormStore.next_step('images')
    }
}

const back = () =>{
    businessRequestFormStore.prev_step('address')
}

const descriptionHelper = async () =>{
    let payload = {
        urgency: getUrgency(urgencyFormData.value.urgency),
        title: titleFormData.value.title,
        city : addressFormData.value.addressItem.city
    }

    try {
        const data = await getDescriptionFromOpenAI(payload)
        lastTitleUsed.value = titleFormData.value.title
        return data.response
    } catch (error) {
        return null
    }
}

const getUrgency = (urgency:string) =>{
    switch (urgency) {
        case "g_lst_bizreq_urgency_1_high":
            return "HIGH"
        case "g_lst_bizreq_urgency_2_std":
            return "MEDIUM" 
        case "g_lst_bizreq_urgency_3_flex":
            return "LOW"
        default:
            return "MEDIUM"
    }
}

const onFocus = () =>{
    hasFocused.value = true
}

const isFormValid = () : boolean => {
    return descriptionFormData.value.description.length != 0 && hasFocused.value
}

</script>

<template>
    <div class="rounded main fullHeight" style="height: 550px">
        <BusinessRequestProgressBar />
        <div class="fullRemainingHeight">
            <div class="expanded">
                <div class="h-style h3-style use-poppins text-center mt-3">
                    {{ t("w_txt_business_request_describe_project") }}
                </div>
                <div class="mt-3 mx-md-2">
                    <p class="">
                        {{ t("w_txt_business_request_short_info") }} <br>
                        {{ t("w_txt_business_request_description_overview") }}
                    </p>
                </div>
                <div class="px-md-2 py-2" v-if="!isLoading">
                    <textarea 
                        :rows="6"
                        :maxlength="1024"
                        type="text"
                        @focus="onFocus" 
                        v-model="descriptionFormData.description"
                        class="form-control message mb-0 text-start resizable-textarea"
                        :placeholder="t('w_txt_csBusinessRequestDescriptionPlaceholder')">
                    </textarea>
                    <p class="mb-0 text-danger" v-if="formErrors.description && descriptionFormData.description.length <= 30">{{ formErrors.description }}</p>
                </div>
                <div v-else style="display: flex; justify-content: center; align-items: center;" class="mt-5">
                    <div>
                    <vue-loaders-ball-spin-fade-loader color="#FEB23C" scale="1"></vue-loaders-ball-spin-fade-loader>
                    </div>
                </div>
            </div>
            <div style="margin-top: auto; margin-bottom:15px ">
                <button @click="back"  type="button" class="btn btn-outline-secondary on-action-btn me-3 mt-2 mb-2 custom-btn-width">
                    {{ t("w_act_business_request_previous") }}
                </button>

                <button @click="validateForm"  
                    type="button" class="btn btn-primary mt-2 mb-2 on-action-btn custom-btn-width"
                    :class="{ 'invalid-btn-style': !isFormValid()}">
                    {{ t("w_act_business_request_next") }}
                </button>
            </div>
        </div>

    </div>
</template>

<style scoped>
.btn-primary:focus{
    background-color: #FEB23C !important;
}
textarea:focus { 
    outline: none !important;
    border-color: #FEB23C;
    border: #FEB23C 2px solid;
}

.main {
    padding-top: 15px;
    position: relative;
}

.bg {
    background-color: #FEB23C !important;
}

.fullHeight {
    height: 100%;
    z-index: 1px;
    border: none !important;
    display: flex;                  
    flex-direction: column;
}

.fullRemainingHeight {
    display: flex;                  
    flex-direction: column; 
    height: 100%;  
}

.expanded{
    flex-grow: 1;
    margin-left: 10px;
    margin-right: 10px;
}

.custom-btn-width {
    min-width: 130px;
}

/* Medium devices (md) - 768px and up */
@media (min-width: 768px) {
    /* Styles for medium screens */
    .expanded{
        margin-left: 50px !important;
        margin-right: 50px !important;
    }

    .custom-btn-width {
        min-width: 150px !important;
    }
}
</style>